import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MDRBadge } from "@/components/MDRBadge";
import { CsvIOButtons } from "@/components/admin/CsvIOButtons";
import { toast } from "sonner";
import { examTypesData, examTypeStatuses, statusLabels } from "@/data/adminConfigData";
import { FileText, Plus, Pencil, Trash2, FlaskConical, FileSignature, UserCheck } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  testing: "bg-amber-50 text-amber-700 border-amber-200",
  attivo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  non_attivo: "bg-rose-50 text-rose-700 border-rose-200"
};

const empty = { name: "", description: "", isInstrumental: false, requiresReport: false, isVisit: false, status: "draft" };

const csvColumns = [
  { key: "id", label: "id" },
  { key: "name", label: "nome" },
  { key: "description", label: "descrizione" },
  { key: "isInstrumental", label: "strumentale", get: (r) => r.isInstrumental ? "si" : "no", parse: (v) => v.trim().toLowerCase() === "si" },
  { key: "requiresReport", label: "refertabile", get: (r) => r.requiresReport ? "si" : "no", parse: (v) => v.trim().toLowerCase() === "si" },
  { key: "isVisit", label: "visita", get: (r) => r.isVisit ? "si" : "no", parse: (v) => v.trim().toLowerCase() === "si" },
  { key: "status", label: "stato" }
];

export const AdminExamTypesPage = () => {
  const [items, setItems] = useState(examTypesData);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const startEdit = (it) => {
    setEditing(it ? { ...it } : { ...empty, id: `et${Date.now()}` });
    setOpen(true);
  };
  const save = () => {
    if (!editing.name) { toast.error("Inserire nome tipologia"); return; }
    setItems(prev => {
      const exists = prev.find(p => p.id === editing.id);
      return exists ? prev.map(p => p.id === editing.id ? editing : p) : [...prev, editing];
    });
    setOpen(false);
    toast.success("Tipologia salvata");
  };
  const remove = (id) => {
    setItems(prev => prev.filter(p => p.id !== id));
    toast.success("Tipologia rimossa");
  };

  const handleImport = (parsed) => {
    setItems(parsed.map(p => ({
      ...empty,
      ...p,
      status: examTypeStatuses.includes(p.status) ? p.status : "draft"
    })));
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-4 w-4 text-slate-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">6.1 Configurazione</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Tipologie esami</h1>
          <p className="text-sm text-muted-foreground mt-1">Categorie merceologiche per prestazioni e bundle</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CsvIOButtons rows={items} columns={csvColumns} filename="tipologie_esami" onImport={handleImport} testIdPrefix="examtypes-csv" />
          <Button variant="premium" onClick={() => startEdit(null)} className="gap-2" data-testid="examtypes-new-btn">
            <Plus className="h-4 w-4" /> Nuova tipologia
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {items.map(t => (
          <Card key={t.id} data-testid={`examtype-card-${t.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base">{t.name}</CardTitle>
                <Badge variant="outline" className={STATUS_STYLE[t.status]}>{statusLabels[t.status]}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">{t.description}</p>
              <div className="flex flex-wrap gap-1">
                {t.isInstrumental && <Badge variant="secondary" className="gap-1 text-[10px]"><FlaskConical className="h-3 w-3" /> Strumentale</Badge>}
                {t.requiresReport && <Badge variant="secondary" className="gap-1 text-[10px]"><FileSignature className="h-3 w-3" /> Refertabile</Badge>}
                {t.isVisit && <Badge variant="secondary" className="gap-1 text-[10px]"><UserCheck className="h-3 w-3" /> Visita</Badge>}
              </div>
              <div className="flex justify-end gap-1 pt-2 border-t">
                <Button variant="ghost" size="icon" onClick={() => startEdit(t)} data-testid={`examtype-edit-${t.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(t.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing && items.find(p => p.id === editing.id) ? "Modifica tipologia" : "Nuova tipologia"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Nome</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="examtype-form-name" />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="space-y-2 p-3 rounded-md border bg-muted/30">
                <div className="flex items-center justify-between">
                  <Label className="text-sm cursor-pointer">Esame strumentale</Label>
                  <Switch checked={editing.isInstrumental} onCheckedChange={(v) => setEditing({ ...editing, isInstrumental: v })} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm cursor-pointer">Esame da refertare</Label>
                  <Switch checked={editing.requiresReport} onCheckedChange={(v) => setEditing({ ...editing, requiresReport: v })} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm cursor-pointer">Visita</Label>
                  <Switch checked={editing.isVisit} onCheckedChange={(v) => setEditing({ ...editing, isVisit: v })} />
                </div>
              </div>
              <div>
                <Label>Stato</Label>
                <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background"
                  value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  {examTypeStatuses.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="examtype-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminExamTypesPage;
